// Rollable tables at the tabletop (issue 1158): the DM's calls, to this origin only through `requestJson` (so
// `authFetch`). The site's tables for the campaign's system and the DM's own (kept with the account); the roll is the
// server's — the browser sends which table and the dice bag's Private choice, and the result arrives in the log.
import { parseUserTable, type TableSource, type UserTable, type RollableTable } from '@dragons-table/vtt-shared';
import { requestJson } from './api';

export interface SiteTable { slug: string; name: string }
export type Answer<T> = { kind: 'ok'; value: T } | { kind: 'refused'; reason: string } | { kind: 'error' };

const enc = encodeURIComponent;
const DOWN = "The tables can't be read just now. Try again in a moment.";
const reasonOf = (status: number, body: unknown, fallback: string): Answer<never> => {
  const r = (body as { reason?: unknown } | null)?.reason;
  if (status === 0) return { kind: 'error' };
  return { kind: 'refused', reason: typeof r === 'string' ? r : fallback };
};

/** The site's tables for the table's game system, by name; the DM's alone. */
export async function siteTables(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<Answer<SiteTable[]>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/rolltables`, {}, fetchImpl);
  const list = (body as { site?: unknown } | null)?.site;
  if (status !== 200 || !Array.isArray(list)) return reasonOf(status, body, DOWN);
  return { kind: 'ok', value: list.filter((t): t is SiteTable => typeof t?.slug === 'string' && typeof t?.name === 'string') };
}

/** The DM's own tables, each read through the shared parser (one it refuses is left out). */
export async function myTables(fetchImpl: typeof fetch = fetch): Promise<Answer<UserTable[]>> {
  const { status, body } = await requestJson('/api/me/rolltables', {}, fetchImpl);
  const list = (body as { tables?: unknown } | null)?.tables;
  if (status !== 200 || !Array.isArray(list)) return reasonOf(status, body, DOWN);
  return {
    kind: 'ok',
    value: list.flatMap((t) => {
      const id = (t as { id?: unknown } | null)?.id;
      const parsed = parseUserTable({ name: (t as { name?: unknown }).name, outcomes: (t as { outcomes?: unknown }).outcomes });
      return typeof id === 'string' && parsed ? [{ id, ...parsed }] : [];
    }),
  };
}

/** Save a new table (no id) or replace one of the DM's own. */
export async function saveTable(table: RollableTable, id: string | null, fetchImpl: typeof fetch = fetch): Promise<Answer<UserTable>> {
  const { status, body } = await requestJson(id ? `/api/me/rolltables/${enc(id)}` : '/api/me/rolltables', { method: id ? 'PUT' : 'POST', body: JSON.stringify(table) }, fetchImpl);
  const got = body as UserTable | null;
  if ((status === 200 || status === 201) && got && typeof got.id === 'string') return { kind: 'ok', value: got };
  if (status === 400) return { kind: 'refused', reason: 'Every outcome needs some text and a weight from 1 to 100, and the table a name.' };
  return reasonOf(status, body, DOWN);
}

export async function deleteTable(id: string, fetchImpl: typeof fetch = fetch): Promise<Answer<null>> {
  const { status, body } = await requestJson(`/api/me/rolltables/${enc(id)}`, { method: 'DELETE' }, fetchImpl);
  return status === 204 ? { kind: 'ok', value: null } : reasonOf(status, body, DOWN);
}

/**
 * Roll a table: the server reads it, throws the die and posts the result to the log, private or public as asked. The
 * value is the outcome's text from the DM's own answer (the quick NPC's card, issue 1157), or null when it carried none.
 */
export async function rollTable(campaignId: string, table: TableSource, priv: boolean, fetchImpl: typeof fetch = fetch): Promise<Answer<string | null>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/rolltables/roll`, { method: 'POST', body: JSON.stringify({ table, private: priv }) }, fetchImpl);
  if (status !== 201) return reasonOf(status, body, "That table couldn't be rolled just now.");
  const text = (body as { roll?: { table?: { text?: unknown } } } | null)?.roll?.table?.text;
  return { kind: 'ok', value: typeof text === 'string' ? text : null };
}
